import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  dlLog,
  exit,
  notifyCheckInReady,
  notifyCheckInUnavailable,
  notifyCheckInFailed,
  onCheckInShow,
  toast,
  waitBridge
} from '@/utils/bridge'
import { CheckInInfo, getInfo, popupAck, sign } from './services'
import shellUrl from './images/shell.webp'
import crownUrl from './images/card-signed-wide.webp'
import closeUrl from './images/close.webp'
import './index.scss'

const TAG = 'CheckIn'
const STATUS_SIGNED = 2

function CheckInPage() {
  const { t } = useTranslation()
  const [info, setInfo] = useState<CheckInInfo>()
  const [signing, setSigning] = useState(false)
  const visibleRef = useRef(false)
  const showTokenRef = useRef(0)
  const ackedTokenRef = useRef(-1)
  const signingRef = useRef(false)

  const applyInfo = useCallback((data: CheckInInfo) => {
    const days = (Array.isArray(data.days) ? data.days : []).map((item) => ({
      day: item.day,
      status: item.status,
      isToday: item.isToday,
      rewardIcon: item.rewardIcon,
      rewardAmount: item.rewardAmount,
      rewardName: item.rewardName
    }))
    days.sort((a, b) => a.day - b.day)
    setInfo({
      eligible: data.eligible,
      endReason: data.endReason,
      timezone: data.timezone,
      serverTime: data.serverTime,
      windowEndTime: data.windowEndTime,
      streak: data.streak,
      todayChecked: data.todayChecked,
      days
    })
  }, [])

  const acknowledge = useCallback(async (token: number) => {
    if (ackedTokenRef.current === token) return
    ackedTokenRef.current = token
    dlLog('info', TAG, 'popupAck start')
    try {
      const res = await popupAck()
      if (!res || res.code !== 200) {
        ackedTokenRef.current = -1
        dlLog('warn', TAG, `popupAck fail code:${res?.code ?? -1}`)
        return
      }
      dlLog('info', TAG, 'popupAck success')
    } catch {
      ackedTokenRef.current = -1
      dlLog('error', TAG, 'popupAck error')
    }
  }, [])

  const loadInfo = useCallback(async (showToken?: number) => {
    dlLog('info', TAG, 'getInfo start')
    try {
      const res = await getInfo()
      if (!res || res.code !== 200 || !res.data) {
        dlLog('warn', TAG, `getInfo fail code:${res?.code ?? -1}`)
        notifyCheckInFailed()
        return
      }
      const data = res.data
      dlLog(
        'info',
        TAG,
        `getInfo eligible:${data.eligible} streak:${data.streak} todayChecked:${data.todayChecked} reason:${data.endReason}`
      )
      if (data.eligible !== 1) {
        notifyCheckInUnavailable()
        return
      }
      applyInfo(data)
      notifyCheckInReady()
      if (visibleRef.current && showToken != null) {
        acknowledge(showToken)
      }
    } catch {
      dlLog('error', TAG, 'getInfo error')
      notifyCheckInFailed()
    }
  }, [acknowledge, applyInfo])

  useEffect(() => {
    const html = document.documentElement
    const body = document.body
    const root = document.getElementById('root')
    const previousHtml = html.style.background
    const previousBody = body.style.background
    const previousRoot = root?.style.background ?? ''
    html.style.background = 'transparent'
    body.style.background = 'transparent'
    if (root) root.style.background = 'transparent'
    dlLog('info', TAG, 'page open')
    let cancelled = false
    const start = async () => {
      if (!window.bridge && (window.isAndroid || window.isIOS)) {
        await waitBridge()
      }
      if (cancelled) return
      onCheckInShow(() => {
        visibleRef.current = true
        showTokenRef.current += 1
        dlLog('info', TAG, `show token:${showTokenRef.current}`)
        loadInfo(showTokenRef.current)
      })
      loadInfo()
    }
    start()
    return () => {
      cancelled = true
      html.style.background = previousHtml
      body.style.background = previousBody
      if (root) root.style.background = previousRoot
    }
  }, [loadInfo])

  const today = info?.days?.find((item) => item.isToday === 1)
  const canSign = !!info && info.todayChecked !== 1 && today?.status !== STATUS_SIGNED && !signing

  const onSign = async () => {
    if (signingRef.current || !canSign) {
      dlLog('info', TAG, 'sign ignored')
      return
    }
    signingRef.current = true
    setSigning(true)
    dlLog('info', TAG, `sign click day:${today?.day ?? 0}`)
    try {
      const res = await sign()
      if (!res || res.code !== 200 || !res.data) {
        dlLog('warn', TAG, `sign fail code:${res?.code ?? -1}`)
        toast(t('Common.SystemError'))
        return
      }
      const data = res.data
      dlLog('info', TAG, `sign result:${data.result} rewardStatus:${data.rewardStatus} eligible:${data.eligible}`)
      if (data.eligible !== 1) {
        notifyCheckInUnavailable()
        return
      }
      applyInfo(data)
      if (data.result === 2) {
        toast(t('CheckIn.Already'))
      } else if (data.result === 4) {
        toast(t('CheckIn.Risk'))
      } else if (data.result === 3) {
        toast(t('CheckIn.Unavailable'))
        notifyCheckInUnavailable()
      } else if (data.result === 1 && data.rewardStatus === 2) {
        toast(t('CheckIn.Pending'))
      }
    } catch {
      dlLog('error', TAG, 'sign error')
      toast(t('Common.NetworkError'))
    } finally {
      signingRef.current = false
      setSigning(false)
    }
  }

  const onClose = () => {
    dlLog('info', TAG, 'close')
    exit()
  }

  const onBlankClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement
    if (target.closest('.pg-check-in__hit, .pg-check-in__crown, .pg-check-in__card, .pg-check-in__title, .pg-check-in__subtitle, .pg-check-in__action, .pg-check-in__close')) {
      return
    }
    dlLog('info', TAG, 'blank close')
    exit()
  }

  const title = t('CheckIn.Title')
  const checkedToday = info?.todayChecked === 1

  return (
    <div className="pg-check-in" onClick={onBlankClick}>
      <div className="pg-check-in__frame">
        <img className="pg-check-in__crown" src={crownUrl} alt="" />
        <img className="pg-check-in__shell" src={shellUrl} alt="" />
        <div className="pg-check-in__hit pg-check-in__hit--panel" />
        <div className="pg-check-in__hit pg-check-in__hit--seven" />
        <div className="pg-check-in__title">
          <span className="pg-check-in__title-text">
            <span className="pg-check-in__title-shadow" aria-hidden="true">{title}</span>
            <span className="pg-check-in__title-fill">{title}</span>
          </span>
        </div>
        {(info?.days ?? []).map((item) => {
          const signed = item.status === STATUS_SIGNED
          const wide = item.day === 7
          return (
            <div
              className={`pg-check-in__card ${signed ? 'is-signed' : 'is-open'} ${wide ? 'is-wide' : ''} is-day-${item.day}`}
              key={item.day}
            >
              {signed && <span className="pg-check-in__fire" />}
              <div className="pg-check-in__body">
                <div className="pg-check-in__day">{signed ? '' : t('CheckIn.Day', { day: item.day })}</div>
                {item.rewardIcon
                  ? <img className="pg-check-in__icon" src={item.rewardIcon} alt="" />
                  : <span className="pg-check-in__icon" />}
                <div className={`pg-check-in__amount ${signed ? 'is-signed' : ''}`}>x{item.rewardAmount}</div>
              </div>
            </div>
          )
        })}
        <div className="pg-check-in__subtitle">{t('CheckIn.Subtitle')}</div>
        <button
          className={`pg-check-in__action ${checkedToday ? 'is-done' : ''}`}
          type="button"
          disabled={!canSign}
          onClick={onSign}
        >
          {checkedToday ? t('CheckIn.Already') : t('CheckIn.Action')}
        </button>
        <button className="pg-check-in__close" type="button" onClick={onClose} aria-label="close">
          <img className="pg-check-in__close-icon" src={closeUrl} alt="" />
        </button>
      </div>
    </div>
  )
}

export default CheckInPage
